import { type SxProps, type Theme } from "@mui/material";

import { FIELD_STACK_GAP } from "ui/forms/input-control/input-control.styles";
import { type RangePickerSize } from "ui/range-picker/range-picker.types";

/** Figma's `date-picker/date-range/spacing/*`. The tokens carry no size, so both sizes share them. */
export const RANGE_SPACING = {
  label: 0.5,
  doubleField: 1.25,
  sublabel: 0.75,
};

export const ROOT_SX: SxProps<Theme> = { gap: RANGE_SPACING.label };

/** `flex-start`, so a message under one field does not stretch the other. */
export const FIELDS_SX: SxProps<Theme> = {
  gap: RANGE_SPACING.doubleField,
  alignItems: "flex-start",
};

/**
 * Each field is a whole `ui/date-picker`, so its label is `FieldLabel` — bold, where Figma sets the
 * sub-label regular under the bold group label.
 *
 * `InputControl` spaces label, control and messages with one `gap`. Figma keeps control → message on
 * that gap but sets the sub-label `RANGE_SPACING.sublabel` above the control at both sizes, so only the
 * label row takes the difference. The asterisk is the group label's alone: Figma hides the sub-labels'.
 */
const getFieldSx =
  (size: RangePickerSize): SxProps<Theme> =>
  (theme) => ({
    flex: "1 1 0",
    minWidth: 0,
    "& .MuiFormLabel-root": { fontWeight: theme.typography.fontWeightRegular },
    "& .MuiFormLabel-asterisk": { display: "none" },
    "& > :has(> .MuiFormLabel-root)": {
      marginBottom: theme.spacing(RANGE_SPACING.sublabel - FIELD_STACK_GAP[size]),
    },
  });

export const FIELD_SX: Record<RangePickerSize, SxProps<Theme>> = {
  medium: getFieldSx("medium"),
  small: getFieldSx("small"),
};
